import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => localStorage.getItem('token');

const chatApi = axios.create({
  baseURL: config.api.chatServiceUrl || 'http://localhost:8008',
  timeout: 60000
});

chatApi.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers['Authorization'] = `Bearer ${t}`;
  return cfg;
});

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(chatApi);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(chatApi);

// Simple in-memory cache for models. Keeps entries for CACHE_TTL ms.
// Each cache entry may hold { ts, data, promise } so concurrent callers
// can share an in-flight promise and avoid duplicate network requests.
const MODELS_CACHE = new Map();
const CACHE_TTL = 1000 * 60 * 60; // 60 minutes
// Conversations cache: short TTL and dedupe in-flight requests to avoid duplicate
const CONV_CACHE = { ts: null, data: null, promise: null };
const CONV_TTL = 1000 * 5; // 5 seconds

const chatService = {
  getModels: async (mode, force = false) => {
    // We now fetch all modes/providers in one call and cache the full payload.
    const key = '__all_models__';
    const now = Date.now();

    // If we have a cache entry
    if (MODELS_CACHE.has(key)) {
      const entry = MODELS_CACHE.get(key);
      if (!force && entry && entry.ts && (now - entry.ts) < CACHE_TTL && entry.data) {
        return entry.data;
      }
      // If there's an in-flight promise, return it so callers share the request
      if (entry && entry.promise) {
        return entry.promise;
      }
    }

    // Create an in-flight promise and store it so concurrent callers reuse it
    const p = chatApi.get('/chat/models').then(r => {
      const data = r.data;
      MODELS_CACHE.set(key, { ts: Date.now(), data, promise: null });
      return data;
    }).catch(err => {
      // Clean up cache on error
      MODELS_CACHE.delete(key);
      throw err;
    });

    MODELS_CACHE.set(key, { ts: null, data: null, promise: p });
    return p;
  },

  // Ask the backend (using the quick/default agent) to generate clarification
  // questions for a user's query. Backend endpoint is expected at POST /chat/clarify
  // and should accept { user_query, agent } where agent defaults to 'quick'.
  // If the backend does not implement this endpoint yet, the frontend will
  // still show a local modal to collect additional context from the user.
  getClarification: async (userQuery, agent = 'quick') => {
    const r = await chatApi.post('/chat/clarify', { user_query: userQuery, agent });
    return r.data;
  },

  // Ask the backend to enhance the prompt for better research instructions.
  getEnhancement: async (userQuery, agent = 'quick') => {
    const r = await chatApi.post('/chat/enhance', { user_query: userQuery, agent });
    return r.data;
  },

  // Optional: allow clearing cache from outside (useful for manual refresh)
  clearModelsCache: (mode) => {
    // We only have a single combined cache entry
    MODELS_CACHE.delete('__all_models__');
  },

  sendMessage: async (payload) => {
    const r = await chatApi.post('/chat/send', payload);
    return r.data;
  },

  renameConversation: async (conversationId, name) => {
    const r = await chatApi.put(`/chat/conversations/${conversationId}/rename`, { conversation_name: name });
    return r.data;
  },

  deleteConversation: async (conversationId) => {
    const r = await chatApi.delete(`/chat/conversations/${conversationId}`);
    // Clear conversations cache after delete
    CONV_CACHE.ts = null;
    CONV_CACHE.data = null;
    CONV_CACHE.promise = null;
    return r.data;
  },

  listConversations: async () => {
    // Default implementation returns fresh data (no caching) unless callers
    // use the new listConversationsCached wrapper below. Keep for backward
    // compatibility.
    const r = await chatApi.get('/chat/conversations');
    return r.data.conversations;
  },

  // Cached and deduped variant: callers can pass `force` to bypass cache.
  listConversationsCached: async (force = false) => {
    const now = Date.now();
    if (!force && CONV_CACHE.ts && CONV_CACHE.data && (now - CONV_CACHE.ts) < CONV_TTL) {
      return CONV_CACHE.data;
    }
    if (CONV_CACHE.promise) {
      return CONV_CACHE.promise;
    }
    const p = chatApi.get('/chat/conversations').then(r => {
      const data = r.data.conversations;
      CONV_CACHE.ts = Date.now();
      CONV_CACHE.data = data;
      CONV_CACHE.promise = null;
      return data;
    }).catch(err => {
      CONV_CACHE.promise = null;
      throw err;
    });
    CONV_CACHE.promise = p;
    return p;
  },

  getConversation: async (conversationId) => {
    const r = await chatApi.get(`/chat/conversations/${conversationId}`);
    return r.data;
  },

  getResponseStatus: async (responseId) => {
    const r = await chatApi.get(`/chat/response-status/${responseId}`);
    return r.data;
  },

  exportMessageToPdf: async (messageId, content, conversationName) => {
    try {
      const requestBody = {
        message_id: messageId,
        content: content,
        conversation_name: conversationName || 'Conversation'
      };

      // Use arraybuffer responseType to properly handle binary PDF data
      const response = await chatApi.post('/chat/export-message-pdf', requestBody, {
        responseType: 'arraybuffer'
      });
      
      // Convert arraybuffer to blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      return blob;
    } catch (error) {
      console.error('Error exporting message to PDF:', error);
      throw error;
    }
  }
};

export default chatService;
